import { FormattedMessage } from 'react-intl';
import { Button, ButtonColor, ButtonVariant, Size } from '@eliberty/design-system-react';

import { PURCHASE_STEP } from '~constants';
import { OrderItemType, Simulation } from '~types/appsync.types';
import { ConsumerCategoryConfirmationState, Participant } from '~types/participant';

interface NextStepButtonProps {
  simulation?: Simulation;
  configurationStep: PURCHASE_STEP;
  participants: Participant[];
  loading: boolean;
  isSimulationOnError: boolean;
  finalPrice?: number;
  fullWidth?: boolean;
  onClickAddToCart: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  onClickAddOptions: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  consumerCategoryConfirmationState?: ConsumerCategoryConfirmationState | null;
  hasSelectedUpsell?: boolean;
}

const NextStepButton = ({
  simulation,
  configurationStep,
  participants,
  loading,
  isSimulationOnError,
  finalPrice,
  fullWidth = false,
  onClickAddToCart,
  onClickAddOptions,
  consumerCategoryConfirmationState,
  hasSelectedUpsell = false,
}: NextStepButtonProps) => {
  const hasBookingOptionWithoutTimeslot = participants.some((p) =>
    p.options.some((option) => option.isBooking && !option.timeslot),
  );

  const selectableOptions =
    simulation?.availableOptions?.filter(
      (option) => OrderItemType.Support !== option.type && !option.mandatory,
    ) ?? [];
  const optionCount = selectableOptions.length;
  const shouldDisplayOptionsFlow = optionCount > 0;

  let label = <FormattedMessage id='addToCart' defaultMessage='Add to cart' />;
  let onClick = onClickAddToCart;
  let variant: ButtonVariant | undefined = undefined;
  let color = ButtonColor.Accent;
  let showLoading = loading;
  let disabled =
    isSimulationOnError ||
    finalPrice === undefined ||
    showLoading ||
    (hasBookingOptionWithoutTimeslot && configurationStep === PURCHASE_STEP.OPTIONS);

  if (consumerCategoryConfirmationState?.isEditing) {
    label = <FormattedMessage id='confirm' defaultMessage='Confirm' />;
    onClick = consumerCategoryConfirmationState.confirm;
    color = ButtonColor.Primary;
    variant = undefined;
    disabled = consumerCategoryConfirmationState.confirmDisabled;
    showLoading = false;
  } else if (shouldDisplayOptionsFlow) {
    if (configurationStep === PURCHASE_STEP.OPTIONS) {
      label = <FormattedMessage id='addToCart' defaultMessage='Add to cart' />;
      onClick = onClickAddToCart;
    } else {
      label = <FormattedMessage id='continue' defaultMessage='Continue' />;
      onClick = onClickAddOptions;
    }
  } else if (configurationStep === PURCHASE_STEP.UPSELL && hasSelectedUpsell) {
    // Upsell selected but no options available → "Add to cart" via continue (will go to OPTIONS then cart)
    label = <FormattedMessage id='addToCart' defaultMessage='Add to cart' />;
    onClick = onClickAddToCart;
  }

  return (
    <Button
      size={Size.M}
      color={color}
      variant={variant}
      onClick={onClick}
      loading={showLoading}
      disabled={disabled}
      fullWidth={fullWidth}
    >
      {label}
    </Button>
  );
};

export default NextStepButton;
